import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  OnInit,
  DestroyRef,
} from "@angular/core";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { catchError, take } from "rxjs/operators";
import { firstValueFrom, of } from "rxjs";
import { FormlyFieldConfig } from "@ngx-formly/core";

import { JobCardComponent } from "src/app/shared/components/network-card/job-card.component";
import {
  SearchContainerComponent,
  SearchDataSource,
  SearchParams,
  SearchResult,
  SearchState,
  buildApiParams,
} from "src/app/shared/components/search";

// API models and services
import { JobPosting } from "src/app/api/models/job-posting";
import { JobPostingPagedData } from "src/app/api/models/job-posting-paged-data";
import { JobPostingService } from "src/app/api/services/job-posting.service";
import { GetJobPostings$Params } from "src/app/api/fn/job-posting/get-job-postings";

// Services
import { DomainService } from "src/app/shared/services/domain.service";
import { StudentService } from "src/app/shared/services/student.service";
import { UserService } from "src/app/shared/services/user.service";
import { AuthenticationService } from "src/app/shared/services/authentication.service";
import { FilterOptionsService } from "src/app/shared/services/filter-options.service";
import { TargetGroupService } from "src/app/shared/services/target-group.service";
import { RoleTypes } from "src/app/guards/roletypes";

@Component({
  selector: "app-student-jobs",
  providers: [SearchState],
  imports: [
    RouterModule,
    TranslateModule,
    SearchContainerComponent,
    JobCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-search-container
      [config]="searchConfig()"
      [dataSource]="dataSource"
      [fields]="fields()"
      [trackByFn]="trackByJobId"
      [showMatchFilter]="true"
    >
      <!-- "My saved jobs" link via content projection -->
      <ng-template #filterPanelTop>
        <a
          routerLink="/student/jobfavorites"
          class="btn btn-danger btn-block mb-3"
        >
          {{ "My saved jobs" | translate }}
        </a>
      </ng-template>

      <!-- Single template handles both grid (portrait) and map popup (landscape) -->
      <ng-template
        #itemTemplate
        let-job
        let-isHighlighted="isHighlighted"
        let-layout="layout"
      >
        <app-job-card
          [data]="job"
          [roleType]="RoleTypes.Student"
          [isHighlighted]="isHighlighted"
          [showMatchPercentage]="layout === 'landscape'"
          [layout]="layout ?? 'portrait'"
          (buttonEvent)="handleJobButtonEvent($event)"
        ></app-job-card>
      </ng-template>
    </app-search-container>
  `,
})
export class StudentJobsComponent implements OnInit {
  private readonly domainService = inject(DomainService);
  private readonly jobPostingService = inject(JobPostingService);
  private readonly studentService = inject(StudentService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchState = inject(SearchState);
  private readonly filterOptionsService = inject(FilterOptionsService);
  private readonly targetGroupService = inject(TargetGroupService);

  // Constants
  readonly RoleTypes = RoleTypes;
  private readonly studentProfile = toSignal(this.authService.studentprofile$);

  // Formly fields configuration using shared field builders
  // Order matches original: Search → Location → Radius → Job types → Field of interest → Education level → Amount employees
  fields = computed<FormlyFieldConfig[]>(() => {
    // Read signal inside computed so field config reactively updates when profile loads (#17797)
    const hideRadius = this.targetGroupService.isInternationalOnly();
    return [
      this.filterOptionsService.searchTermField(),
      this.filterOptionsService.buildSearchModeField(() => hideRadius),
      this.filterOptionsService.buildSearchModeLocationField({
        label: "Location job",
      }),
      this.filterOptionsService.buildSearchModeRadiusField(
        this.searchState.searchOptions().radiusOptions,
        () => this.studentProfile() ?? null,
        () => hideRadius
      ),
      this.filterOptionsService.jobPostingTypesField(),
      this.filterOptionsService.areaOfInterestsField(),
      this.filterOptionsService.educationLevelsField(),
      // TODO: Re-enable when backend supports TargetGroups filter on /jobposting endpoint
      // this.filterOptionsService.targetGroupsField(),
      this.filterOptionsService.employeeRangeField(),
    ];
  });

  // Search config - map filtering handled automatically by search infrastructure
  searchConfig = computed(() => ({
    searchOptions: this.searchState.searchOptions(),
    context: {
      queryParamOptions: [
        "Location",
        "MaximumDistance",
        "AreaOfInterests",
        "EducationLevels",
        "Favorites",
        "SearchTerm",
        "NumberOfEmployeesLower",
        "NumberOfEmployeesUpper",
        "JobPostingType",
        "MinimumMatchPercentage",
        "TargetGroups",
      ],
    },
    filterDisplayNames: {
      SearchTerm: "Search",
      MaximumDistance: "Radius",
      Location: "Location job",
    },
    excludeFromBadges: ["MinimumMatchPercentage", "searchMode"],
    valueFormatters: {
      MaximumDistance: (value: string | number | boolean) => `${value} km`,
      MinimumMatchPercentage: (value: string | number | boolean) => `${value}%`,
    },
    mapConfig: {
      enabled: true,
      getCoordinates: (job: JobPosting) => {
        const addr = job.address;
        if (addr?.latitude != null && addr?.longitude != null) {
          return { lat: addr.latitude, lng: addr.longitude };
        }
        return null;
      },
    },
  }));

  // Simple data source - client-side distance filtering handled by search infrastructure
  dataSource: SearchDataSource<JobPosting> = {
    fetch: async (params: SearchParams): Promise<SearchResult<JobPosting>> => {
      const apiParams = buildApiParams<GetJobPostings$Params>(
        params,
        this.domainService.getCurrentRegion()
      );

      const result = await firstValueFrom(
        this.jobPostingService
          .getJobPostings(apiParams)
          .pipe(
            catchError(() =>
              of({ data: [], totalItems: 0 } as JobPostingPagedData)
            )
          )
      );

      return {
        data: result?.data || [],
        totalItems: result?.totalItems || 0,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      };
    },
  };

  ngOnInit(): void {
    this.prefillAreaOfInterests();
    this.applyInternationalFilterIfNeeded();
  }

  /**
   * Apply International filter by default for EU region users who haven't opted to show all vacancies.
   * Task #17796: Student profiles marked as 'international'
   * Now uses account settings (DisableTargetGroupsJobs) instead of profile settings
   */
  private applyInternationalFilterIfNeeded(): void {
    // Only apply for EU region (international domain)
    if (this.domainService.getCurrentRegion() !== "EU") {
      return;
    }

    // Check student's preference - if DisableTargetGroupsJobs is false, apply International filter
    this.userService
      .apiGetAccountCurrent()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((account: any) => {
        if (account && account.disableTargetGroupsJobs === false) {
          // Apply default International filter for EU students who haven't opted to show all
          this.searchState.updateFilters({ TargetGroups: ["International"] });
        }
      });
  }

  private prefillAreaOfInterests(): void {
    // Only prefill if no area of interests are already set (e.g., from URL params)
    const currentFilters = this.searchState.filters();
    const areaOfInterests = currentFilters["AreaOfInterests"];
    if (Array.isArray(areaOfInterests) && areaOfInterests.length) {
      return;
    }

    // Try to get from current student profile
    if (this.studentService.currentStudentprofile?.areaOfInterests?.length) {
      const interests =
        this.studentService.currentStudentprofile.areaOfInterests.map(
          (item: any) => item.name
        );
      this.searchState.updateFilter("AreaOfInterests", interests);
    } else {
      // Fallback to auth service
      this.authService.studentprofile$
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe((profile) => {
          if (profile?.areaOfInterests?.length) {
            const interests = profile.areaOfInterests.map(
              (item: any) => item.name
            );
            this.searchState.updateFilter("AreaOfInterests", interests);
          }
        });
    }
  }

  handleJobButtonEvent(event: {
    event: string;
    data: string | JobPosting;
  }): void {
    if (event?.event === "applyjobposting") {
      // Application is handled within NetworkJobComponent modal
      // No additional handling needed here
    }
  }

  readonly trackByJobId = (_index: number, job: JobPosting): string =>
    job.id || "";
}
